/**
 * LoadingSpinner - Loading State Component
 * 
 * Purpose: Consistent loading UI across the application
 * Dependencies: @shared/utils (cn)
 * Exports: LoadingSpinner
 * Side Effects: React rendering
 * Safety: 🟢 SAFE - Presentational component
 * 
 * Agent Notes:
 * - Keep variants minimal (small, medium, large, fullScreen)
 * - Uses Tailwind CSS animation utilities
 * - Responsive sizing with Tailwind classes
 * - Last updated: 2026-01-20 (Tailwind CSS migration)
 */

import { cn } from '@shared/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
  message?: string;
  className?: string;
}

export function LoadingSpinner({
  size = 'md',
  fullScreen = false,
  message,
  className,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-6 h-6 border-2',
    md: 'w-12 h-12 border-4',
    lg: 'w-16 h-16 border-4',
  };

  const spinner = (
    <div className={cn('flex flex-col items-center justify-center gap-4', className)} role="status">
      <span className="sr-only">Loading...</span>
      <div className={cn(
        'border-gray-300 border-t-blue-600 rounded-full animate-spin',
        sizeClasses[size]
      )} />
      {message && <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white dark:bg-gray-800 bg-opacity-75 dark:bg-opacity-90 z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
}
